import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApolloError } from "@apollo/client";
import { ChartSpline, CircleAlert, FileDown, Share } from "lucide-react";
import { toast } from "sonner";
import { HorizontalStackedBarChart } from "@/components/HorizontalStackedBarChart";
import { ChartDataItem } from "@/components/HorizontalStackedBarChart/types";
import {
  groupStatesByFunnelGroup,
  FunnelStateMaps,
  FunnelMapKeys,
} from "@/util/funnelGroups";
import {
  FunnelStateEnum,
  PredictionIndexEnum,
  RisePredictiveIndexEnum,
  PartnerFeatureEnum,
  YesNoEnum,
} from "@/__generated__/types";
import { FilterState } from "@/components/FilterSidebar/FilterState";
import { useProtectedNavToStudent } from "@/routes/buildStudentsListLink";
import { EmptyStateComponent } from "@/components/EmptyStateComponent";
import { exportChartCSV, timestampFilename } from "@/util/exportChartCSV";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { useIndexSummary } from "@/hooks/useIndexSummary";
import { PredictiveModelType } from "./types";
import type { IndexType } from "@/hooks/useIndexSummary";

import {
  chartConfig,
  seriesConfig,
  axisConfig,
  tabsConfig,
  getFunnelFilterValue,
  getRiseFunnelStates,
  CHART_TAB_KEYS,
  CHART_SERIES_KEYS,
  ChartTabKey,
} from "./config/chartConfig";
import { Skeleton, Card, CardContent, CardHeader } from "@/components/ui/";
import { Switch } from "@/components/ui/Switch";
import { FilterInputData } from "@/components/FilterSidebar/types";

type ChartData = Record<ChartTabKey | "default", ChartDataItem[]>;
type FunnelStateData = { state: FunnelStateEnum; value: number }[];

const getCommitmentIndexLevel = (
  categoryValue: string,
): PredictionIndexEnum => {
  const level = categoryValue.toLowerCase();
  switch (level) {
    case "high":
      return PredictionIndexEnum.High;
    case "medium":
      return PredictionIndexEnum.Medium;
    case "low":
      return PredictionIndexEnum.Low;
    default:
      return PredictionIndexEnum.Low;
  }
};

const getRiseCommitmentIndexLevel = (
  categoryValue: string,
): RisePredictiveIndexEnum => {
  const level = categoryValue.toLowerCase();
  switch (level) {
    case "high":
      return RisePredictiveIndexEnum.High;
    case "medium-high":
      return RisePredictiveIndexEnum.MediumHigh;
    case "medium":
      return RisePredictiveIndexEnum.Medium;
    case "medium-low":
      return RisePredictiveIndexEnum.MediumLow;
    case "low":
      return RisePredictiveIndexEnum.Low;
    default:
      return RisePredictiveIndexEnum.Low;
  }
};

interface IndexSummaryData {
  low: FunnelStateData | null;
  mediumLow?: FunnelStateData | null;
  medium: FunnelStateData | null;
  mediumHigh?: FunnelStateData | null;
  high: FunnelStateData | null;
}

interface ChartSectionProps {
  orgId: string | undefined;
  orgIdLoading: boolean;
  orgIdError: ApolloError | undefined;
  cohortYear: number | undefined;
  cohortLoading: boolean;
  cohortError: ApolloError | undefined;
  modelType?: PredictiveModelType;
}

export const ChartSection: React.FC<ChartSectionProps> = ({
  orgId,
  orgIdLoading,
  orgIdError,
  cohortYear,
  cohortLoading,
  cohortError,
  modelType = "Commit",
}) => {
  const { t } = useTranslation();
  const { hasOrgFeature } = useOrgPermissions();
  const isRiseEnabled = hasOrgFeature(PartnerFeatureEnum.RisePredictiveModels);

  const [onlyInCommunity, setOnlyInCommunity] = useState(false);

  const getIndexType = useCallback(
    (type: PredictiveModelType): IndexType => {
      if (!isRiseEnabled) return "commit";

      const indexMap: Record<PredictiveModelType, IndexType> = {
        Commit: "riseCommit",
        Apply: "riseApply",
        Melt: "riseMelt",
        Yield: "riseYield",
      };

      return indexMap[type];
    },
    [isRiseEnabled],
  );

  const indexType = getIndexType(modelType);

  const {
    data: indexSummaryData,
    loading: indexLoading,
    error: indexError,
  } = useIndexSummary(
    indexType,
    orgId,
    cohortYear,
    isRiseEnabled && onlyInCommunity ? YesNoEnum.Yes : undefined,
  );

  const transformFunnelDataToChart = (
    orgData: IndexSummaryData | null | undefined,
  ): ChartData => {
    if (!orgData) {
      return {
        default: [],
        [CHART_TAB_KEYS.ADMITS_ONLY]: [],
        [CHART_TAB_KEYS.APPLICANTS_ONLY]: [],
        [CHART_TAB_KEYS.ALL_STUDENTS]: [],
      };
    }

    const { low, medium, high, mediumLow, mediumHigh } = orgData;

    const isApplyIndex = isRiseEnabled && modelType === "Apply";
    const committedKey = isApplyIndex
      ? CHART_SERIES_KEYS.APPLIED
      : CHART_SERIES_KEYS.COMMITTED;
    const notCommittedKey = isApplyIndex
      ? CHART_SERIES_KEYS.NOT_APPLIED
      : CHART_SERIES_KEYS.NOT_COMMITTED;

    const createLevelData = (
      levelData: { state: FunnelStateEnum; value: number }[] | null | undefined,
      levelName: string,
    ): ChartDataItem => {
      if (!levelData || !Array.isArray(levelData)) {
        return {
          level: levelName,
          [committedKey]: 0,
          [notCommittedKey]: 0,
        };
      }

      if (isApplyIndex) {
        const levelGrouped = groupStatesByFunnelGroup(
          levelData,
          "RISE_APPLY_INDEX_CHART",
        );
        return {
          level: levelName,
          [committedKey]: levelGrouped.allStudentsApplied || 0,
          [notCommittedKey]: levelGrouped.allStudents || 0,
        };
      }

      const mappingKey = isRiseEnabled
        ? "RISE_INDEX_CHART"
        : "COMMIT_INDEX_CHART";
      const levelGrouped = groupStatesByFunnelGroup(levelData, mappingKey);

      return {
        level: levelName,
        [committedKey]: levelGrouped.allStudentsCommitted || 0,
        [notCommittedKey]: levelGrouped.allStudents || 0,
      };
    };

    const createTabLevelData = (
      levelData: { state: string; value: number }[] | null | undefined,
      levelName: string,
      committedGroupKey: keyof ReturnType<
        typeof groupStatesByFunnelGroup<"RISE_INDEX_CHART">
      >,
      notCommittedGroupKey: keyof ReturnType<
        typeof groupStatesByFunnelGroup<"RISE_INDEX_CHART">
      >,
    ): ChartDataItem => {
      if (!levelData || !Array.isArray(levelData)) {
        return {
          level: levelName,
          [committedKey]: 0,
          [notCommittedKey]: 0,
        };
      }

      const convertedData: FunnelStateData = levelData.map((item) => ({
        state: item.state as FunnelStateEnum,
        value: item.value,
      }));

      const levelGrouped = groupStatesByFunnelGroup(
        convertedData,
        "RISE_INDEX_CHART",
      );

      return {
        level: levelName,
        [committedKey]: levelGrouped[committedGroupKey] || 0,
        [notCommittedKey]: levelGrouped[notCommittedGroupKey] || 0,
      };
    };

    const createAdmitsOnlyLevelData = (
      levelData: { state: string; value: number }[] | null | undefined,
      levelName: string,
    ): ChartDataItem => {
      return createTabLevelData(
        levelData,
        levelName,
        "admitsOnlyCommitted",
        "admitsOnly",
      );
    };

    const createApplicantsOnlyLevelData = (
      levelData: { state: string; value: number }[] | null | undefined,
      levelName: string,
    ): ChartDataItem => {
      return createTabLevelData(
        levelData,
        levelName,
        "applicantsOnlyCommitted",
        "applicantsOnly",
      );
    };

    if (isRiseEnabled && mediumHigh && mediumLow) {
      const levels = [
        { data: high, name: "High" },
        { data: mediumHigh, name: "Medium-High" },
        { data: medium, name: "Medium" },
        { data: mediumLow, name: "Medium-Low" },
        { data: low, name: "Low" },
      ];

      return {
        default:
          modelType === "Apply"
            ? levels.map(({ data, name }) => createLevelData(data, name))
            : [],
        [CHART_TAB_KEYS.ADMITS_ONLY]: levels.map(({ data, name }) =>
          createAdmitsOnlyLevelData(data, name),
        ),
        [CHART_TAB_KEYS.APPLICANTS_ONLY]: levels.map(({ data, name }) =>
          createApplicantsOnlyLevelData(data, name),
        ),
        [CHART_TAB_KEYS.ALL_STUDENTS]: levels.map(({ data, name }) =>
          createLevelData(data, name),
        ),
      };
    }

    return {
      default:
        modelType === "Apply"
          ? [
              createLevelData(high, "High"),
              createLevelData(medium, "Medium"),
              createLevelData(low, "Low"),
            ]
          : [],
      [CHART_TAB_KEYS.ADMITS_ONLY]: [
        createAdmitsOnlyLevelData(high, "High"),
        createAdmitsOnlyLevelData(medium, "Medium"),
        createAdmitsOnlyLevelData(low, "Low"),
      ],
      [CHART_TAB_KEYS.APPLICANTS_ONLY]: [],
      [CHART_TAB_KEYS.ALL_STUDENTS]: [
        createLevelData(high, "High"),
        createLevelData(medium, "Medium"),
        createLevelData(low, "Low"),
      ],
    };
  };

  const hasActualError = orgIdError || cohortError || indexError;
  const hasMissingDependencies =
    !orgIdLoading && !cohortLoading && (!orgId || !cohortYear);
  const isLoading = orgIdLoading || cohortLoading || indexLoading;

  const hasEmptyData = (() => {
    if (hasActualError || hasMissingDependencies) return false;
    if (!indexSummaryData) return true;
    if (
      !indexSummaryData.low ||
      !indexSummaryData.medium ||
      !indexSummaryData.high
    ) {
      return true;
    }

    return false;
  })();

  const chartData = transformFunnelDataToChart(indexSummaryData);

  const navToStudent = useProtectedNavToStudent();

  const handleClick = useCallback(
    (categoryValue: string, seriesKey: string, tabValue: string) => {
      if (!cohortYear) return;

      const isApplyIndex = isRiseEnabled && modelType === "Apply";
      const isCommittedOrApplied = isApplyIndex
        ? seriesKey === CHART_SERIES_KEYS.APPLIED
        : seriesKey === CHART_SERIES_KEYS.COMMITTED;

      const actualTabKey = isApplyIndex ? "default" : (tabValue as ChartTabKey);

      if (isRiseEnabled) {
        const riseIndex = getRiseCommitmentIndexLevel(categoryValue);
        const lowerModelType = modelType.toLowerCase();

        const funnelStateEnums = getRiseFunnelStates(
          modelType,
          actualTabKey,
          isCommittedOrApplied,
        );

        const sidebarGroups = new Set<FunnelMapKeys<"SIDEBAR">>();
        for (const funnelEnum of funnelStateEnums) {
          const groups = Object.entries(FunnelStateMaps.SIDEBAR).filter(
            ([, enums]) => enums.includes(funnelEnum),
          );
          groups.forEach(([groupName]) =>
            sidebarGroups.add(groupName as FunnelMapKeys<"SIDEBAR">),
          );
        }

        const filterData: Partial<FilterInputData> = {
          cohortYear: [cohortYear.toString()],
          funnelStates: Array.from(sidebarGroups),
        };

        if (lowerModelType === "apply") {
          filterData.applyIndex = [riseIndex];
        } else if (lowerModelType === "commit") {
          filterData.riseCommitIndex = [riseIndex];
        } else if (lowerModelType === "melt") {
          filterData.meltIndex = [riseIndex];
        } else if (lowerModelType === "yield") {
          filterData.yieldIndex = [riseIndex];
        }

        if (onlyInCommunity) {
          filterData.inCommunity = YesNoEnum.Yes;
        }

        const filterState = new FilterState(filterData);
        navToStudent({ fs: filterState });
      } else {
        const commitIndex = getCommitmentIndexLevel(categoryValue);
        const validFunnelSidebarStates = getFunnelFilterValue(
          actualTabKey as ChartTabKey,
          isCommittedOrApplied,
        );

        const filterState = new FilterState({
          cohortYear: [cohortYear.toString()],
          commitIndex: [commitIndex],
          funnelStates: validFunnelSidebarStates,
        });

        navToStudent({ fs: filterState });
      }
    },
    [cohortYear, navToStudent, isRiseEnabled, modelType, onlyInCommunity],
  );

  const handleExportCSV = useCallback(() => {
    if (!chartData || hasEmptyData || hasActualError) {
      toast.error("No data available to export");
      return;
    }

    try {
      const isApplyIndex = isRiseEnabled && modelType === "Apply";
      const levels = [
        { key: "High", translationKey: "predictiveModels.chart.category.high" },
        ...(isRiseEnabled
          ? [
              {
                key: "Medium-High",
                translationKey: "predictiveModels.chart.category.mediumHigh",
              },
            ]
          : []),
        {
          key: "Medium",
          translationKey: "predictiveModels.chart.category.medium",
        },
        ...(isRiseEnabled
          ? [
              {
                key: "Medium-Low",
                translationKey: "predictiveModels.chart.category.mediumLow",
              },
            ]
          : []),
        { key: "Low", translationKey: "predictiveModels.chart.category.low" },
      ];

      const getSeriesKey = (isCommitted: boolean) =>
        isApplyIndex
          ? isCommitted
            ? CHART_SERIES_KEYS.APPLIED
            : CHART_SERIES_KEYS.NOT_APPLIED
          : isCommitted
            ? CHART_SERIES_KEYS.COMMITTED
            : CHART_SERIES_KEYS.NOT_COMMITTED;

      const getTranslationKey = (isCommitted: boolean) => {
        const type = isApplyIndex
          ? isCommitted
            ? "applied"
            : "notApplied"
          : isCommitted
            ? "committed"
            : "notCommitted";
        return `predictiveModels.chart.commitmentType.${type}`;
      };

      const commitmentTypes = [
        { key: getSeriesKey(true), translationKey: getTranslationKey(true) },
        { key: getSeriesKey(false), translationKey: getTranslationKey(false) },
      ];

      const headers = isApplyIndex
        ? ["", "Count"]
        : [
            "",
            t("predictiveModels.chart.tabs.admitsOnly"),
            ...(isRiseEnabled
              ? [t("predictiveModels.chart.tabs.applicantsOnly")]
              : []),
            t("predictiveModels.chart.tabs.allStudents"),
          ];

      const exportData = levels.flatMap((level) =>
        commitmentTypes.map((commitmentType) => {
          if (isApplyIndex) {
            return {
              "": `${t(level.translationKey)} - ${t(commitmentType.translationKey)}`,
              Count:
                chartData.default.find((d) => d.level === level.key)?.[
                  commitmentType.key
                ] || 0,
            };
          }

          const row: Record<string, string | number> = {
            "": `${t(level.translationKey)} - ${t(commitmentType.translationKey)}`,
            [t("predictiveModels.chart.tabs.admitsOnly")]:
              chartData[CHART_TAB_KEYS.ADMITS_ONLY].find(
                (d) => d.level === level.key,
              )?.[commitmentType.key] || 0,
            [t("predictiveModels.chart.tabs.allStudents")]:
              chartData[CHART_TAB_KEYS.ALL_STUDENTS].find(
                (d) => d.level === level.key,
              )?.[commitmentType.key] || 0,
          };

          if (isRiseEnabled) {
            row[t("predictiveModels.chart.tabs.applicantsOnly")] =
              chartData[CHART_TAB_KEYS.APPLICANTS_ONLY].find(
                (d) => d.level === level.key,
              )?.[commitmentType.key] || 0;
          }

          return row;
        }),
      );

      exportChartCSV({
        filename: timestampFilename(`${modelType.toLowerCase()}-index-summary`),
        headers,
        data: exportData,
      });

      toast.success(t("global.exportToCSV.success"));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error(t("global.exportToCSV.failure"));
    }
  }, [chartData, hasEmptyData, hasActualError, t, isRiseEnabled, modelType]);

  const menuOptions = useMemo(
    () => [
      {
        label: t("global.exportToCSV.menuTitle"),
        onClick: handleExportCSV,
        icon: <FileDown aria-hidden height={16} />,
      },
      {
        label: t("predictiveModels.chart.menuOptions.share"),
        onClick: () => {
          toast.success(t("predictiveModels.chart.menuOptions.successCopied"));
          navigator.clipboard.writeText(window.location.href);
        },
        icon: <Share aria-hidden height={16} />,
      },
    ],
    [t, handleExportCSV],
  );

  if (isLoading) {
    return (
      <Card data-testid="commitment-index-chart-wrapper" className="h-[474px]">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <Skeleton className="h-8 w-8" />
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col">
          <div className="mb-4 flex flex-col-reverse items-start justify-between gap-2 sm:flex-row sm:items-center">
            <div className="flex gap-2">
              <Skeleton className="h-9 w-24" />
              <Skeleton className="h-9 w-24" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Skeleton className="h-2 w-5" />
                <Skeleton className="h-4 w-16" />
              </div>
              <div className="flex items-center gap-1">
                <Skeleton className="h-2 w-5" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          </div>
          <div className="flex flex-1 flex-col justify-center gap-8">
            <Skeleton className="h-18 w-full" />
            <Skeleton className="h-18 w-full" />
            <Skeleton className="h-18 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (hasActualError || hasMissingDependencies) {
    return (
      <Card
        data-testid="commitment-index-chart-wrapper"
        className="flex h-[474px] flex-col"
      >
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">
                {t("predictiveModels.chart.title", {
                  index: t("predictiveModels.chart.indexes.commit"),
                })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("predictiveModels.chart.defaultSubtitle", {
                  index: t("predictiveModels.chart.indexes.commit"),
                })}
              </p>
            </div>
            <div className="h-8 w-8" />
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 items-center justify-center">
          <EmptyStateComponent
            message={t("predictiveModels.chart.error.loadingError")}
            icon={<CircleAlert className="size-12" />}
          />
        </CardContent>
      </Card>
    );
  }

  if (hasEmptyData) {
    return (
      <Card
        data-testid="commitment-index-chart-wrapper"
        className="flex h-[474px] flex-col"
      >
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">
                {t("predictiveModels.chart.title", {
                  index: t("predictiveModels.chart.indexes.commit"),
                })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("predictiveModels.chart.defaultSubtitle", {
                  index: t("predictiveModels.chart.indexes.commit"),
                })}
              </p>
            </div>
            <div className="h-8 w-8" />
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 items-center justify-center">
          <EmptyStateComponent
            message={t("predictiveModels.chart.emptyMessage")}
            icon={<ChartSpline className="size-12" />}
          />
        </CardContent>
      </Card>
    );
  }

  const dataToShow =
    isRiseEnabled && modelType === "Apply"
      ? { default: chartData.default }
      : chartData;

  const currentTabsConfig = tabsConfig(t, modelType, isRiseEnabled);

  const indexName = t(
    `predictiveModels.chart.indexes.${modelType.toLowerCase()}`,
  );
  const chartTitle = t("predictiveModels.chart.title", { index: indexName });
  const chartSubtitle = t("predictiveModels.chart.defaultSubtitle", {
    index: indexName,
  });

  return (
    <div data-testid="commitment-index-chart-wrapper">
      <HorizontalStackedBarChart
        title={chartTitle}
        subtitle={chartSubtitle}
        data={dataToShow}
        chartConfig={chartConfig(t, modelType, isRiseEnabled)}
        seriesConfig={seriesConfig(t, modelType, isRiseEnabled)}
        axisConfig={axisConfig(t)}
        tabsConfig={currentTabsConfig}
        stateKey={modelType}
        onSegmentClick={handleClick}
        menuOptions={menuOptions}
        footerContent={
          isRiseEnabled ? (
            <div className="flex items-center space-x-2 pb-6 pl-6">
              <Switch
                id="only-in-community"
                checked={onlyInCommunity}
                onCheckedChange={setOnlyInCommunity}
              />
              <label
                htmlFor="only-in-community"
                className="text-sm font-medium text-foreground"
              >
                {t("predictiveModels.chart.onlyInCommunity")}
              </label>
            </div>
          ) : undefined
        }
      />
    </div>
  );
};
